import { globalLoader } from '@hotjar/webapp/components/GlobalLoader/GlobalLoader';
import { i18next } from '@hotjar/translations';
import { isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';
import { subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import type { OrderAction } from '@hotjar/webapp/domains/billing/orders/api';
import type { OwnedAccount } from '@hotjar/account';
import type { PaymentData } from '@hotjar/webapp/billing/plan';
import type { VaultedPaymentMethod } from '@hotjar/webapp/billing/payment';
import { ordersApi } from '@hotjar/webapp/domains/billing/orders';
import { openCompanyDetailsModal } from '../CompanyDetails';
import { openPaymentDetails } from '../PaymentDetails';
import { authenticatePaymentMethod as authenticatePaymentMethodWith3DS } from '../braintree/3ds';
import type { OnSuccessResponse } from '../PaymentDetails/PaymentDetailsModal';
import type { FormInputs } from '../CompanyDetails/CompanyDetailsForm/CompanyDetailsForm';
import type { PlanChanges } from './planChanges';
import { triggerPlanChanges } from './planChanges';

export async function handlePaymentMethodVaulted({
  paymentData,
  account,
  vaultedPaymentMethod,
  billingCycle,
}: {
  paymentData: PaymentData;
  account: OwnedAccount;
  vaultedPaymentMethod: VaultedPaymentMethod;
  billingCycle?: IBillingCycle;
}): Promise<void> {
  try {
    globalLoader.show({ message: i18next.t('billing:checkout.contacting-bank') });
    const newPriceSummary = await subscriptionAPI.preview(
      account,
      paymentData.costSpecs,
      billingCycle,
    );
    const authenticatedPaymentMethod = await authenticatePaymentMethodWith3DS(
      account,
      vaultedPaymentMethod,
      newPriceSummary.future.totalWithTax,
    );
    await subscriptionAPI.upsert({
      accountId: account.id as number,
      costSpecs: paymentData.costSpecs,
      paymentMethod: authenticatedPaymentMethod as VaultedPaymentMethod,
      billingCycle,
    });
  } finally {
    globalLoader.hide();
  }
}

interface AddPaymentMethodAndUpsert {
  account: OwnedAccount;
  paymentData: PaymentData;
  initialBillingCycle?: IBillingCycle;
  planChanges: PlanChanges;
  errorMessage?: string;
  showCompanyDetailsModal?: boolean;
}

export async function addPaymentMethodAndUpsert({
  paymentData,
  account,
  initialBillingCycle,
  planChanges,
  errorMessage,
  showCompanyDetailsModal = true,
}: AddPaymentMethodAndUpsert): Promise<unknown> {
  const zuoraEnabled = isZuoraEnabled(account);

  const companyDetails = await (showCompanyDetailsModal
    ? openCompanyDetailsModal()
    : Promise.resolve(null));

  const paymentMethodResult = await openPaymentDetails(zuoraEnabled, {
    account,
    costSpecs: paymentData.costSpecs,
    initialBillingCycle,
    country: (companyDetails as FormInputs)?.country,
    referrer: paymentData.referrer,
    initialErrorMessage: errorMessage,
    onPaymentMethodVaulted: async ({ vaultedPaymentMethod, billingCycle: adjustedBillingCycle }) =>
      handlePaymentMethodVaulted({
        billingCycle: adjustedBillingCycle,
        account,
        paymentData,
        vaultedPaymentMethod,
      }),
  });

  triggerPlanChanges(planChanges);

  if (zuoraEnabled) {
    const { companyName, taxNumber, ...billingAddress } = companyDetails as FormInputs;
    const { refId, billingCycle } = paymentMethodResult as OnSuccessResponse;

    const createOrderRequest = {
      orderAction: 'customer_and_subscription_creation_order' as OrderAction.create,
      subscription: {
        billingPeriod: billingCycle as 'monthly' | 'yearly_recurring',
        /* eslint-disable camelcase */
        sitePlans: (paymentData.costSpecs ?? []).map(({ site_id, plan_id }) => ({
          siteId: site_id,
          planId: plan_id,
        })),
        /* eslint-enable camelcase */
      },
      customer: {
        companyName,
        taxNumber,
        billingAddress,
        additionalEmailAddresses: [], // TODO: add
        paymentMethod: {
          // TODO: for now zuora only supports credit card, this will change
          paymentMethodType: 'credit_card' as const,
          paymentMethodId: refId,
        },
      },
    };

    return ordersApi.createNewOrder(account.id, createOrderRequest);
  }

  return null;
}
